import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { UserCheck, Loader2, Clock, Search, ArrowLeft, ExternalLink } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function InteressesPage() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filterCommercial, setFilterCommercial] = useState(null);
    const [allCommerciaux, setAllCommerciaux] = useState([]);

    useEffect(() => {
        axios.get(`${API}/api/devis/interesses`, { withCredentials: true })
            .then(({ data }) => setItems(data))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
        axios.get(`${API}/api/inventaires/config`, { withCredentials: true })
            .then(({ data }) => setAllCommerciaux(data.commerciaux || []))
            .catch(() => {});
    }, []);

    const commercialList = (() => {
        const stats = {};
        allCommerciaux.forEach(c => {
            stats[c.id] = { id: c.id, name: c.name, couleur: c.couleur || '#94a3b8', count: 0 };
        });
        items.forEach(item => {
            const key = item.commercial_id || '_none';
            if (!stats[key]) {
                stats[key] = { id: item.commercial_id, name: item.commercial_name || 'Non assigné', couleur: item.commercial_couleur || '#94a3b8', count: 0 };
            }
            stats[key].count++;
        });
        return Object.values(stats).sort((a, b) => b.count - a.count);
    })();

    const filtered = items.filter(item => {
        const matchSearch = !search.trim() || (item.client_nom || '').toLowerCase().includes(search.toLowerCase());
        const matchCommercial = !filterCommercial || (item.commercial_id || '_none') === filterCommercial;
        return matchSearch && matchCommercial;
    });

    return (
        <div className="p-4 md:p-6 lg:p-8" data-testid="interesses-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                <div className="flex items-center gap-3">
                    <Button variant="outline" size="sm" onClick={() => navigate('/devis')} data-testid="interesses-back-btn">
                        <ArrowLeft size={16} className="mr-1.5" />
                        Retour
                    </Button>
                    <div>
                        <h1
                            className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight"
                            style={{ fontFamily: 'Manrope, sans-serif' }}
                            data-testid="interesses-title"
                        >
                            Clients intéressés
                        </h1>
                        <p className="mt-1 text-slate-500">
                            {items.length} client{items.length > 1 ? 's' : ''} intéressé{items.length > 1 ? 's' : ''}
                        </p>
                    </div>
                </div>
            </div>

            {/* Commercial cards */}
            {commercialList.length > 0 && (
                <div className="grid gap-3 mb-6" style={{ gridTemplateColumns: `repeat(${commercialList.length}, 1fr)` }} data-testid="interesses-commercial-stats">
                    {commercialList.map(c => (
                        <Card
                            key={c.id || '_none'}
                            className={`rounded-lg border shadow-sm cursor-pointer transition-all hover:shadow-md ${
                                filterCommercial === (c.id || '_none')
                                    ? 'ring-2 ring-offset-1 bg-slate-50'
                                    : 'bg-white border-slate-200'
                            }`}
                            style={filterCommercial === (c.id || '_none') ? { borderColor: c.couleur } : {}}
                            onClick={() => setFilterCommercial(prev => prev === (c.id || '_none') ? null : (c.id || '_none'))}
                            data-testid={`interesses-commercial-card-${c.id || 'none'}`}
                        >
                            <CardContent className="px-4 py-3 flex items-center gap-2.5">
                                <div
                                    className="w-9 h-9 rounded-md flex items-center justify-center shrink-0"
                                    style={{ backgroundColor: c.couleur + '20' }}
                                >
                                    <span className="text-sm font-bold" style={{ color: c.couleur }}>{c.count}</span>
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="text-sm font-semibold text-slate-800 truncate">{c.name}</p>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Search */}
            <Card className="bg-white rounded-xl border border-slate-200 shadow-sm mb-6">
                <CardContent className="p-4">
                    <div className="relative flex-1">
                        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input
                            placeholder="Rechercher par nom..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-10"
                            data-testid="interesses-search"
                        />
                    </div>
                </CardContent>
            </Card>

            {/* Table */}
            <Card className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg font-semibold text-slate-800">
                        <UserCheck size={20} className="text-cyan-500" />
                        Clients intéressés ({filtered.length})
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0 sm:p-6">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-[#2e7cd6]" />
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="text-center py-12 text-slate-500">
                            Aucun client intéressé
                        </div>
                    ) : (
                        <div className="overflow-x-auto -mx-0">
                            <Table data-testid="interesses-table" className="min-w-[700px]">
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Client</TableHead>
                                        <TableHead>Commercial</TableHead>
                                        <TableHead>Depuis</TableHead>
                                        <TableHead>Remarque</TableHead>
                                        <TableHead className="text-right"></TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filtered.map(item => (
                                        <TableRow key={item.devis_id} className="bg-cyan-50/30" data-testid={`interesse-row-${item.devis_id}`}>
                                            <TableCell>
                                                <div className="flex items-center gap-3">
                                                    <div className="w-9 h-9 rounded-full bg-cyan-100 flex items-center justify-center shrink-0">
                                                        <UserCheck size={16} className="text-cyan-600" />
                                                    </div>
                                                    <p className="font-medium text-slate-900">{item.client_nom || '-'}</p>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                {item.commercial_name ? (
                                                    <span
                                                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium text-white"
                                                        style={{ backgroundColor: item.commercial_couleur || '#94a3b8' }}
                                                    >
                                                        {item.commercial_name}
                                                    </span>
                                                ) : (
                                                    <span className="text-sm text-slate-400 italic">Non assigné</span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-1.5">
                                                    <Clock size={14} className="text-cyan-500" />
                                                    <span className="text-sm font-medium text-cyan-700">{item.elapsed}</span>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <span className="text-sm text-slate-600">{item.remarque_interesse || '-'}</span>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <Button
                                                    size="sm"
                                                    className="bg-[#2e7cd6] hover:bg-[#2568b8] text-white"
                                                    onClick={() => navigate(`/devis/${item.inventaire_id}`)}
                                                    data-testid={`interesse-go-${item.devis_id}`}
                                                >
                                                    <ExternalLink size={14} className="mr-1.5" />
                                                    Voir devis
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
